import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Check, Key } from "lucide-react";
import { TwoFactorAuthService, TwoFactorAuthStorage } from "../../lib/twoFactorAuth";

export const TwoFactorVerification = (): JSX.Element => {
  const navigate = useNavigate();
  const [totpToken, setTotpToken] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [activeTab, setActiveTab] = useState("totp");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTOTPVerification = async () => {
    const secret = TwoFactorAuthStorage.getSecret();
    if (!secret) {
      setError("2FA not properly configured. Please contact administrator.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const isValid = await TwoFactorAuthService.verifyToken(totpToken, secret);
      
      if (isValid) {
        navigate('/aAyuiOcvMq/dashboard');
      } else {
        setError("Invalid verification code. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error('Error verifying TOTP:', err);
      setError("Error verifying code. Please try again.");
      setLoading(false);
    }
  };

  const handleBackupCodeVerification = () => {
    const backupCodes = TwoFactorAuthStorage.getBackupCodes();
    if (!backupCodes) {
      setError("Backup codes not found. Please contact administrator.");
      return;
    }

    setLoading(true);
    setError(null);

    const isValid = TwoFactorAuthService.verifyBackupCode(backupCode, backupCodes);
    
    if (isValid) {
      // Remove the used backup code
      const updatedCodes = backupCodes.filter(code => 
        code !== backupCode.replace(/\s/g, '').toUpperCase()
      );
      TwoFactorAuthStorage.setBackupCodes(updatedCodes);
      
      navigate('/aAyuiOcvMq/dashboard');
    } else {
      setError("Invalid backup code. Please try again.");
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'totp' | 'backup') => {
    if (e.key === 'Enter') {
      if (type === 'totp') {
        handleTOTPVerification();
      } else {
        handleBackupCodeVerification();
      }
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-screen">
        <div className="w-full max-w-[480px] bg-white rounded-[20px] p-8 shadow-lg mt-[140px]">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-[#daed6e] rounded-full flex items-center justify-center mb-4">
              <img src="/security-icon.svg" alt="2FA" className="w-8 h-8" />
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Two-Factor Authentication
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80 text-center">
              Enter your verification code to access the admin panel
            </p>
          </div>

          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg mb-6">
              {error}
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="totp" className="flex items-center space-x-2">
                <Check className="w-4 h-4" />
                <span>Authenticator</span>
              </TabsTrigger>
              <TabsTrigger value="backup" className="flex items-center space-x-2">
                <Key className="w-4 h-4" />
                <span>Backup Code</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="totp" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Google Authenticator</CardTitle>
                  <CardDescription>
                    Enter the 6-digit code from your Google Authenticator app
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      value={totpToken}
                      onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="text-center text-lg font-mono tracking-widest"
                      maxLength={6}
                      onKeyPress={(e) => handleKeyPress(e, 'totp')}
                    />
                    <Button 
                      onClick={handleTOTPVerification}
                      disabled={totpToken.length !== 6 || loading}
                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    >
                      {loading ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="backup" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Backup Code</CardTitle>
                  <CardDescription>
                    Enter one of your backup codes if you can't access your authenticator
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      type="text"
                      value={backupCode}
                      onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
                      placeholder="XXXX-XXXX"
                      className="text-center text-lg font-mono tracking-widest"
                      onKeyPress={(e) => handleKeyPress(e, 'backup')}
                    />
                    <Button 
                      onClick={handleBackupCodeVerification}
                      disabled={!backupCode || loading}
                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    >
                      {loading ? 'Verifying...' : 'Verify'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Format: XXXX-XXXX (each backup code can only be used once)
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/aAyuiOcvMq/login')}
              className="text-sm"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 